import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  Menu,
  Select,
  Spin,
  Table,
  Tooltip,
} from "antd";
import { ErrorNotificationMsg } from "../../utils/Notifications";
import { getRequest, patchRequest } from "../../axios";
import { authUserData } from "../../utils/Helper";
import { InfoCircleOutlined } from "@ant-design/icons";

import ButtonField from "../../common/htmlComponents/ButtonField";
import Downloadpd from "../../image/Downloadpd.svg";
import Excel from "../../image/excel.png";
import PDF from "../../image/pdf.png";

import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import "jspdf-autotable";
import jsPDF from "jspdf";
import { DownloadTableExcel } from "react-export-table-to-excel";

export const SPECIFIC_GRAVITY = {
  GI: 7.85,
  MS: 7.85,
  CU: 8.9,
  AL: 2.7,
  SS: 8.0,
};

export const DENSITY = {
  GI: 7850,
  MS: 7850,
  CU: 8960,
  AL: 2700,
  SS: 8000,
};

export function getDensity(material) {
  const sg = SPECIFIC_GRAVITY[material.toUpperCase()] || 0;
  return sg * 1000; 
}

export function getKgPerMeter(widthMM, thicknessMM, material) {
  const density = DENSITY[material.toUpperCase()] || 0;
  if (!density || !widthMM || !thicknessMM) return 0;
  return (widthMM * thicknessMM * density) / 1_000_000;
}

const isEarthingMaterialName = (name = "") => {
  const upper = String(name).toUpperCase();
  return (
    /\d+\s*x\s*\d+/i.test(name) &&
    (upper.includes("CU") || upper.includes("GI"))
  );
};

const inferCategoryTreeFromApiData = (apiData) => {
  if (apiData?.category_tree_name) return apiData.category_tree_name;
  const materials =
    apiData?.comparative_details_by_rfq_site_wise?.flatMap((city) =>
      city?.rfq_site_wise_details?.flatMap(
        (site) => site?.rfq_material_wise_details ?? [],
      ),
    ) ?? [];
  if (
    materials.some((m) => isEarthingMaterialName(getMaterialTypeName(m)))
  ) {
    return "Earthing";
  }
  return "General";
};

const getComparativeApiData = (response) => {
  const raw = response?.data?.data ?? response?.data ?? null;
  return normalizeComparativeApiData(raw);
};

const getMaterialTypeName = (material) =>
  material?.rfq_material_type_name ??
  material?.general_rfq_material_type_name ??
  "";

const normalizeMaterialRow = (material) => {
  const name = getMaterialTypeName(material);
  return {
    ...material,
    rfq_material_type_id:
      material?.rfq_material_type_id ?? material?.general_rfq_material_type_id,
    rfq_material_type_name: name,
    rfq_material_type_total_quantity:
      material?.rfq_material_type_total_quantity ??
      material?.general_rfq_material_type_total_quantity,
    rfq_material_type_uom:
      material?.rfq_material_type_uom ?? material?.general_rfq_material_type_uom,
    rfq_material_type_sub_category:
      material?.rfq_material_type_sub_category ??
      material?.general_rfq_material_type_sub_category ??
      "",
  };
};

const hasValidSubTotalRow = (row) =>
  Boolean(
    row?.bid_date &&
      row.bid_date !== "N/A" &&
      Number(row.total_price) > 0,
  );

const ROUND_PRIORITY = { r: 3, r1: 2, r0: 1 };

const getLatestVendorSubTotalRows = (vendorSubTotals = []) => {
  const byVendor = new Map();

  vendorSubTotals.forEach((row) => {
    if (!hasValidSubTotalRow(row)) return;

    const existing = byVendor.get(row.vendor_id);
    if (
      !existing ||
      ROUND_PRIORITY[row.round] > ROUND_PRIORITY[existing.round]
    ) {
      byVendor.set(row.vendor_id, row);
    }
  });

  return [...byVendor.values()];
};

const applyCrossVendorCommercialRankings = (rows = []) => {
  if (!rows.length) return rows;

  const sortedLatestRows = [...getLatestVendorSubTotalRows(rows)].sort(
    (a, b) => (Number(a.total_price) || 0) - (Number(b.total_price) || 0),
  );

  const rankByVendorRound = new Map();
  sortedLatestRows.forEach((row, index) => {
    rankByVendorRound.set(`${row.vendor_id}__${row.round}`, index + 1);
  });

  return rows.map((row) => {
    const rank = rankByVendorRound.get(`${row.vendor_id}__${row.round}`);
    if (rank == null) {
      return row;
    }

    return {
      ...row,
      commercial_ranking: rank,
    };
  });
};

const collectVendorSubTotals = (materials, siteVendorSubTotal) => {
  let rows = [];
  if (Array.isArray(siteVendorSubTotal) && siteVendorSubTotal.length > 0) {
    rows = siteVendorSubTotal;
  } else {
    rows = materials.flatMap((m) =>
      Array.isArray(m?.vendor_sub_total) ? m.vendor_sub_total : [],
    );
    const seen = new Set();
    rows = rows.filter((row) => {
      const key = `${row.vendor_id}__${row.round}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  if (rows.length === 0) return [];

  return applyCrossVendorCommercialRankings(rows);
};

const normalizeComparativeApiData = (raw) => {
  if (!raw) return null;

  const comparative = (raw.comparative_details_by_rfq_site_wise ?? []).map(
    (city) => ({
      ...city,
      rfq_city_name: city?.rfq_city_name ?? "General",
      rfq_site_wise_details: (city?.rfq_site_wise_details ?? []).map((site) => {
        const materials = (site?.rfq_material_wise_details ?? []).map(
          normalizeMaterialRow,
        );
        return {
          ...site,
          rfq_site_name: site?.rfq_site_name ?? "General",
          rfq_material_wise_details: materials,
          vendor_sub_total: collectVendorSubTotals(
            materials,
            site?.vendor_sub_total,
          ),
        };
      }),
    }),
  );

  return {
    ...raw,
    comparative_details_by_rfq_site_wise: comparative,
  };
};

const collectVendorSummary = (apiData) => {
  const materials =
    apiData?.comparative_details_by_rfq_site_wise?.flatMap((city) =>
      city?.rfq_site_wise_details?.flatMap(
        (site) => site?.rfq_material_wise_details ?? [],
      ),
    ) ?? [];

  for (let i = materials.length - 1; i >= 0; i -= 1) {
    const summary = materials[i]?.vendor_summary;
    if (Array.isArray(summary) && summary.length > 0) {
      return summary;
    }
  }
  return [];
};

const buildVendorSummaryTableRows = (vendorSummaryTotals) => {
  if (!vendorSummaryTotals?.length) return [];

  let srCounter = 1;
  const summaryEntries = [
    { keySuffix: "supply_site_wise", title: "Supply - Site Wise" },
    { keySuffix: "freight_site_wise", title: "Freight - Site Wise" },
    {
      keySuffix: "sub_total",
      title: "Sub Total (Site Wise) (Supply + Freight)",
    },
    {
      keySuffix: "supply_site_order_value",
      title: "Supply as a % of respective site order value",
    },
    {
      keySuffix: "freight_site_order_value",
      title: "Freight as a % of respective site order value",
    },
  ];

  return summaryEntries.map((entry) => {
    const rowData = {
      key: `summary-${entry.keySuffix}`,
      srNo: `${srCounter++}`,
      title: entry.title,
      uom: entry.keySuffix,
    };

    vendorSummaryTotals.forEach((vendorDetail) => {
      const vendorKey = String(vendorDetail.vendor_name ?? "")
        .toLowerCase()
        .replace(/\s+/g, "");
      const value = vendorDetail[entry.keySuffix];
      if (
        entry.keySuffix === "supply_site_order_value" ||
        entry.keySuffix === "freight_site_order_value"
      ) {
        rowData[`${vendorKey}Amount`] = `${Number(value ?? 0).toFixed(2)}`;
      } else {
        rowData[`${vendorKey}Amount`] = `${Number(
          value ?? 0,
        ).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
      }
    });

    return rowData;
  });
};

const formatComparativeNumber = (value, { decimals, fallback = "" } = {}) => {
  if (value == null || value === "" || value === "N/A") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  if (decimals != null) return n.toFixed(decimals);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

const isMaterialComparativeRow = (record) =>
  record?.srNo != null && /^\d+$/.test(String(record.srNo));

const parseVendorCellNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const isBlankVendorMaterialValue = (value) => {
  if (React.isValidElement(value)) return false;
  if (value == null || value === "") return true;
  const num = parseVendorCellNumber(value);
  return num == null || num === 0;
};

const renderVendorPriceCell = (value, record) => {
  if (React.isValidElement(value)) {
    return { children: value, props: {} };
  }

  if (!isMaterialComparativeRow(record)) {
    const children =
      typeof value === "number" ? value.toFixed(0) : value ?? "";
    return { children, props: {} };
  }

  if (isBlankVendorMaterialValue(value)) {
    return { children: "N/A", props: {} };
  }

  const children = typeof value === "number" ? value.toFixed(0) : value;
  return { children, props: {} };
};

const ROUND_PRICE_KEYS = [
  { round: "r0", priceKey: "r0_price" },
  { round: "r1", priceKey: "r1_price" },
  { round: "r", priceKey: "r_price" },
];

const hasValidVendorBidRound = (priceEntry) =>
  Boolean(
    priceEntry?.bid_date &&
      priceEntry.bid_date !== "N/A" &&
      Number(priceEntry.r_unit_price) > 0,
  );

const getVendorBidRoundVisibility = (vendor, { showR0, showR1, showR }) => {
  const hasFinalBid = showR
    ? true
    : hasValidVendorBidRound(vendor?.r_price?.[0]);
  const hasRevisedBid = showR1
    ? true
    : !hasValidVendorBidRound(vendor?.r_price?.[0]) &&
      hasValidVendorBidRound(vendor?.r1_price?.[0]);
  const hasInitialBid = showR0
    ? true
    : !hasValidVendorBidRound(vendor?.r_price?.[0]) &&
      !hasValidVendorBidRound(vendor?.r1_price?.[0]) &&
      hasValidVendorBidRound(vendor?.r0_price?.[0]);

  return {
    shouldShowFinal: hasFinalBid || showR,
    shouldShowR1: hasRevisedBid || showR1,
    shouldShowR0: hasInitialBid || showR0,
  };
};

const buildGeneralComparativeFilterPayload = (state) => ({
  material_type_names:
    state.material_type_names?.length > 0 ? state.material_type_names : null,
});

const buildGeneralVendorSubTotals = (
  siteDetail,
  { contingencyPercent = 0, commissionPercent = 0 } = {},
) => {
  const materials = siteDetail?.rfq_material_wise_details ?? [];
  const vendorMap = {};

  materials.forEach((material) => {
    (material?.vendors ?? []).forEach((vendor) => {
      ROUND_PRICE_KEYS.forEach(({ round, priceKey }) => {
        const priceEntry = vendor?.[priceKey]?.[0];
        if (!hasValidVendorBidRound(priceEntry)) return;

        const mapKey = `${vendor.vendor_id}__${round}`;
        if (!vendorMap[mapKey]) {
          vendorMap[mapKey] = {
            vendor_id: vendor.vendor_id,
            vendor_name: vendor.vendor_name,
            round,
            bid_date: priceEntry.bid_date ?? null,
            basic_price: 0,
            commission_charges: 0,
            taxes: 0,
            total_price: 0,
            contingency: 0,
            total_with_tax: 0,
            payment_terms: vendor.payment_terms ?? "—",
            payment_security_details: vendor.payment_security_details ?? "—",
            commercial_ranking: null,
            material_type_details: {},
          };
        }

        const basic =
          Number(priceEntry.supply_site_wise) > 0
            ? Number(priceEntry.supply_site_wise)
            : Number(priceEntry.r_amount_without_gst) || 0;
        const withGst = Number(priceEntry.r_amount_with_gst) || 0;
        vendorMap[mapKey].basic_price += basic;
        vendorMap[mapKey].taxes += Math.max(0, withGst - basic);
        if (!vendorMap[mapKey].bid_date && priceEntry.bid_date) {
          vendorMap[mapKey].bid_date = priceEntry.bid_date;
        }
      });
    });
  });

  const rows = Object.values(vendorMap);
  rows.forEach((row) => {
    if (row.commission_charges == null || row.commission_charges === 0) {
      row.commission_charges =
        (row.basic_price * (Number(commissionPercent) || 0)) / 100;
    }
    if (row.total_price == null || row.total_price === 0) {
      row.total_price = row.basic_price + row.commission_charges + row.taxes;
    }
    if (row.contingency == null) {
      row.contingency =
        (row.total_price * (Number(contingencyPercent) || 0)) / 100;
    }
    if (row.total_with_tax == null || row.total_with_tax === 0) {
      row.total_with_tax = row.total_price + row.contingency;
    }
  });

  ["r0", "r1", "r"].forEach((round) => {
    const roundRows = rows
      .filter((row) => row.round === round)
      .sort((a, b) => a.total_price - b.total_price);
    roundRows.forEach((row, index) => {
      if (row.commercial_ranking == null || row.commercial_ranking > 100) {
        row.commercial_ranking = index + 1;
      }
    });
  });

  return rows;
};

const resolveGeneralVendorSubTotals = (siteDetail, apiData = {}) => {
  let rows = [];

  if (
    Array.isArray(siteDetail?.vendor_sub_total) &&
    siteDetail.vendor_sub_total.length > 0
  ) {
    rows = siteDetail.vendor_sub_total;
  } else {
    const fromMaterials = (siteDetail?.rfq_material_wise_details ?? []).flatMap(
      (m) => (Array.isArray(m?.vendor_sub_total) ? m.vendor_sub_total : []),
    );
    if (fromMaterials.length > 0) {
      const seen = new Set();
      rows = fromMaterials.filter((row) => {
        const key = `${row.vendor_id}__${row.round}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    } else {
      const rfqTotals = apiData?.rfq_vendor_sub_total;
      if (Array.isArray(rfqTotals) && rfqTotals.length > 0) {
        rows = rfqTotals;
      } else {
        rows = buildGeneralVendorSubTotals(siteDetail, {
          contingencyPercent: Number(apiData?.contingency_charges) || 0,
          commissionPercent: Number(apiData?.commission_charges) || 0,
        });
      }
    }
  }

  return applyCrossVendorCommercialRankings(rows);
};

const GeneralProcurementComparative = () => {
  const { t } = useTranslation();
  const { Option } = Select;
  const navigate = useNavigate();
  const CompanyId = authUserData()?.company ? authUserData()?.company : null;
  const [reload, setReload] = useState(false);
  const [materialName, setMaterialName] = useState([]);
  const [enableInputs, setEnableInputs] = useState(false);
  const { id } = useParams();
  const { Search } = Input;
  const [totalCount, settotalCount] = useState(0);
  const [rfqHistory, setRFQHistory] = useState([]);
  const [categoryTree, setCategoryTree] = useState("");
  const [siteDetails, setSiteDetails] = useState(null);
  const [initialRender, setInitialRender] = useState(true);
  const formRef = useRef();
  const [vendors, setVendors] = useState([]);

  const [loading, setLoading] = useState(false);
  const [apiLoading, setapiLoading] = useState(false);
  const [state, setState] = useState({
    material_type_names: [],
  });

  const [hideInitialBidPrice, sethideInitialBidPrice] = useState(false);
  const [hideR1BidPrice, setHideR1BidPrice] = useState(false);
  const [hideRBidPrice, setHideRBidPrice] = useState(false);
  const [showUnitRateCementR1, setShowUnitRateCementR1] = useState(false);
  const [showUnitRateCementR, setShowUnitRateCementR] = useState(false);
  const [isFinalBidPrice, setIsFinalBidPrice] = useState(false);
  const [showAdditionalCheckboxesFinal, setShowAdditionalCheckboxesFinal] =
    useState(true);
  const [showAdditionalCheckboxes, setShowAdditionalCheckboxes] =
    useState(false);
  const [comparativesRFQ, setComparativesRFQ] = useState([]);
  const [rfqNumber, setRfqNumber] = useState(null);
  const [buyerName, setBuyerName] = useState(null);
  const [contengency, setContengency] = useState(null);

  const [response1, setResponse1] = useState([]);
  const [dataSource1, setDataSource] = useState([]);
  const [data, setData] = useState([]);

  const [summaryDataSource, setSummaryDataSource] = useState([]);
  const [showR1, setShowR1] = useState(false);
  const [showR0, setShowR0] = useState(false);
  const [showR, setShowR] = useState(false);
  const [showLPP, setShowLPP] = useState(false);
  const [showGST, setShowGST] = useState(false);
  const tableRef = useRef();
  const hiddenTableRef = useRef();

  const [initialBidDate, setInitialBidDate] = useState(null);
  const [revisedBidDate, setRevisedBidDate] = useState(null);
  const [finalBidDate, setFinalBidDate] = useState(null);

  const [vendorBidDates, setVendorBidDates] = useState({});

  const getVendorBidRFQ = async () => {
    if (!id) return;
    setapiLoading(true);
    try {
      const response1 = await patchRequest(
        `general-comparative-rfq/${id}/get-comparative-details-by-city-wise/`,
      );
      const apiData = getComparativeApiData(response1);
      setCategoryTree(inferCategoryTreeFromApiData(apiData));
      setRfqNumber(apiData.rfq_number);
      setBuyerName(apiData.buyer_name);
      setContengency(apiData.contingency_charges);
      setResponse1(response1);
    } catch (error) {
      console.error("Error:", error);
      ErrorNotificationMsg(t("Error in Comparatives"));
    } finally {
      setapiLoading(false);
    }
  };

  useEffect(() => {
    getVendorBidRFQ();
  }, [id]);

  useEffect(() => {
    if (!response1?.data) return;

    const apiData = getComparativeApiData(response1);
    if (!apiData) {
      ErrorNotificationMsg(t("Error in Comparatives"));
      return;
    }

    {
      const dataSource = [];
      let totalQuantity = 0;
      let materialRowIndex = 0;
      let uniqueKeyCounter = 0;
      const uniqueEntries = new Set();
      const selectedRounds = {};

      const getAvailableRound = (vendorDetail, vendorSubTotals) => {
        if (vendorDetail.bid_date === null) {
          return null; // No valid round available
        } else {
          const vendorKey = vendorDetail.vendor_name
            .toLowerCase()
            .replace(/\s+/g, "");

          if (selectedRounds[vendorKey]) {
            return selectedRounds[vendorKey];
          }

          const vendorRounds1 = vendorSubTotals.filter(
            (v) => v.vendor_name === vendorDetail.vendor_name
          );

          const vendorRounds = vendorSubTotals
            .filter(
              (v) =>
                v.vendor_name === vendorDetail.vendor_name &&
                vendorDetail.bid_date !== null
            )
            .map((v) => v.round);

          const priorityOrder = ["r", "r1", "r0"];
          const selectedRound = priorityOrder.find((round) =>
            vendorRounds.includes(round)
          );

          selectedRounds[vendorKey] = selectedRound || null;

          return selectedRound;
        }
      };

      const findLatestAvailableRound = (vendorDetail, vendorSubTotals) => {
        if (!vendorDetail) return null;

        const vendorRounds = vendorSubTotals
          .filter(
            (v) =>
              v.vendor_name === vendorDetail.vendor_name && v.bid_date !== null
          )
          .sort((a, b) => {
            const roundPriority = { r: 3, r1: 2, r0: 1 };
            return roundPriority[b.round] - roundPriority[a.round];
          });

        return vendorRounds.length > 0 ? vendorRounds[0].round : null;
      };

      const categoryName =
        apiData.category_tree_name ?? inferCategoryTreeFromApiData(apiData);

      const isSand =
        categoryName?.toLowerCase()?.includes("sand") ||
        categoryName?.toLowerCase()?.includes("aggregate");

      const isEarthing = categoryName?.toLowerCase()?.includes("earthing");

      const vendors = (
        apiData.comparative_details_by_rfq_site_wise ?? []
      ).map(
          (cityDetail, cityIndex) => {
            return cityDetail.rfq_site_wise_details.map(
              (siteDetail, siteIndex) => {
                siteDetail.rfq_material_wise_details?.forEach(
                  (materialDetail, materialIndex) => {
                    totalQuantity +=
                      materialDetail.rfq_material_type_total_quantity;

                    const materialVendors = materialDetail.vendors || [];

                    const materialIsEarthing =
                      isEarthing ||
                      isEarthingMaterialName(
                        materialDetail.rfq_material_type_name,
                      );

                    const vendorData = materialVendors.reduce((acc, vendor) => {
                      if (vendor) {
                        const vendorKey = `${vendor.vendor_name
                          .toLowerCase()
                          .replace(/\s+/g, "")}`;
                        const price = vendor?.r_price[0] || {};
                        const priceR0 = vendor?.r0_price[0] || {};
                        const priceR1 = vendor?.r1_price[0] || {};

                        const initialBidDate =
                          vendor.r0_price[0]?.bid_date || "N/A";
                        const revisedBidDate =
                          vendor.r1_price[0]?.bid_date || "N/A";
                        const finalBidDate =
                          vendor.r_price[0]?.bid_date || "N/A";

                        setVendorBidDates((prevState) => ({
                          ...prevState,
                          [vendorKey]: {
                            initialBidDate,
                            revisedBidDate,
                            finalBidDate,
                          },
                        }));

                        const unitPrice = price?.r_unit_price || Infinity;

                        acc[`${vendorKey}UnitPrice`] =
                          unitPrice.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          }) || 0;

                        
                        acc[`${vendorKey}Amount`] =
                          price?.r_amount_without_gst?.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          }) || 0;
                        acc[`${vendorKey}UnitPriceR0`] =
                          priceR0?.r_unit_price?.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          }) || 0;
                        acc[`${vendorKey}AmountR0`] =
                          priceR0?.r_amount_without_gst?.toLocaleString(
                            "en-IN",
                            {
                              maximumFractionDigits: 0,
                            }
                          ) || 0;
                        acc[`${vendorKey}UnitPriceR1`] =
                          priceR1?.r_unit_price?.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          }) || 0;
                        acc[`${vendorKey}AmountR1`] =
                          priceR1?.r_amount_without_gst?.toLocaleString(
                            "en-IN",
                            {
                              maximumFractionDigits: 0,
                            }
                          ) || 0;

                        acc.prices = acc.prices || {};
                        acc.prices[vendorKey] = unitPrice;
                      }
                      return acc;
                    }, {});

                    const vendorPrices = materialVendors.map((vendor) => {
                      const vendorKey = `${vendor.vendor_name
                        .toLowerCase()
                        .replace(/\s+/g, "")}`;
                      let latestPrice = Infinity;

                      if (
                        vendor.r_price[0]?.r_unit_price !== undefined &&
                        vendor.r_price[0]?.bid_date
                      ) {
                        latestPrice = vendor.r_price[0].r_unit_price;
                      } else if (
                        vendor.r1_price[0]?.r_unit_price !== undefined &&
                        vendor.r1_price[0]?.bid_date
                      ) {
                        latestPrice = vendor.r1_price[0].r_unit_price;
                      } else if (
                        vendor.r0_price[0]?.r_unit_price !== undefined &&
                        vendor.r0_price[0]?.bid_date
                      ) {
                        latestPrice = vendor.r0_price[0].r_unit_price;
                      }
                      return { vendorKey, price: latestPrice };
                    });
                    const minPriceVendor = vendorPrices.reduce(
                      (minVendor, currentVendor) => {
                        return currentVendor.price < minVendor.price
                          ? currentVendor
                          : minVendor;
                      },
                      { vendorKey: null, price: Infinity }
                    );

                    const lppEntries =
                      materialDetail.last_purchase_price?.length > 0
                        ? materialDetail.last_purchase_price
                        : [null];

                    lppEntries.forEach((priceDetail, priceIndex) => {
                        const uniqueId = priceDetail
                          ? `${materialDetail.rfq_material_type_name}-${materialDetail.rfq_material_type_id}-${priceDetail.vendor_name}`
                          : `${materialDetail.rfq_material_type_name}-${materialDetail.rfq_material_type_id}`;

                        let wtKgPerMeter = null;
                        let estRate = 0;
                        if (materialIsEarthing) {
                          const { width, thickness, material } =
                            parseEarthingMaterial(
                              materialDetail.rfq_material_type_name
                            );

                          const kgPerMeter = getKgPerMeter(
                            width,
                            thickness,
                            material
                          );

                          const ratePerKg =
                            materialDetail.estimated_material_unit_price || 0;

                          estRate = kgPerMeter * ratePerKg;
                          wtKgPerMeter = kgPerMeter;
                        } else {
                          estRate =
                            materialDetail.estimated_unit_rate ??
                            materialDetail.estimated_material_unit_price ??
                            0;
                        }

                        if (!uniqueEntries.has(uniqueId) && priceIndex === 0) {
                          uniqueEntries.add(uniqueId);

                          materialRowIndex += 1;
                          dataSource.push({
                            key: `lp-${cityIndex}-${siteIndex}-${materialIndex}-${priceIndex}-${uniqueKeyCounter++}`,
                            srNo: `${materialRowIndex}`,
                            items: materialDetail.rfq_material_type_name,
                            gst_rate: materialDetail.gst_rate,
                            qty: materialDetail.rfq_material_type_total_quantity,
                            uom: materialDetail.rfq_material_type_uom,
                            wtKgPerMeter: wtKgPerMeter || null,
                            estRate: Number.isFinite(Number(estRate))
                              ? Number(estRate)
                              : null,
                            materialId: materialDetail.rfq_material_type_id,
                            poDate: priceDetail?.lpp_po_number_date || "N/A",
                            vendorName: priceDetail?.vendor_name || "N/A",
                            unitPrice:
                              priceDetail?.unit_price?.toLocaleString("en-IN", {
                                maximumFractionDigits: 0,
                              }) || 0,
                            avgPrice:
                              priceDetail?.["30_days_avg_price"]?.toLocaleString(
                                "en-IN",
                                {
                                  maximumFractionDigits: 0,
                                }
                              ) || 0,
                            itemWiseL1:
                              priceDetail?.["item_wise_l1"]?.toLocaleString(
                                "en-IN",
                                { maximumFractionDigits: 0 }
                              ) || 0,
                            cityWise:
                              priceDetail?.[
                                "30_days_avg_price_sb"
                              ]?.toLocaleString("en-IN", {
                                maximumFractionDigits: 0,
                              }) || 0,
                            ...vendorData,
                          });
                        }
                      }
                    );

                    dataSource.forEach((item) => {
                      if (
                        item.srNo === "A1" ||
                        item.srNo === "L" ||
                        item.srNo === "L1"
                      )
                        return;

                      if (
                        item.items === materialDetail.rfq_material_type_name
                      ) {
                        let lowestPrice = Infinity;
                        let lowestVendorKey = null;
                        let lowestPriceKey = null;

                        const vendorKeys = Object.keys(item)
                          .filter(
                            (key) =>
                              key.endsWith("UnitPrice") && !key.includes("R")
                          )
                          .map((key) => key.replace("UnitPrice", ""));

                        vendorKeys.forEach((vendorKey) => {
                          const priceKeys = [
                            `${vendorKey}UnitPrice`,
                            `${vendorKey}UnitPriceR1`,
                            `${vendorKey}UnitPriceR0`,
                          ];

                          let latestKey = null;
                          let latestValue = Infinity;

                          for (const key of priceKeys) {
                            let val = item[key];

                            if (
                              typeof val === "object" &&
                              val?.props?.children
                            ) {
                              val = val.props.children;
                            }

                            const numericVal = parseFloat(
                              (val || "0").toString().replace(/,/g, "")
                            );
                            if (!isNaN(numericVal) && numericVal > 0) {
                              latestValue = numericVal;
                              latestKey = key;
                              break;
                            }
                          }

                          if (latestValue < lowestPrice) {
                            lowestPrice = latestValue;
                            lowestVendorKey = vendorKey;
                            lowestPriceKey = latestKey;
                          }
                        });

                        if (lowestPriceKey) {
                          item[lowestPriceKey] = (
                            <span
                              style={{
                                fontWeight: "bolder",
                                color: "green",
                              }}
                            >
                              {item[lowestPriceKey]?.props?.children ||
                                item[lowestPriceKey]}
                            </span>
                          );
                        }
                      }
                    });
                  }
                );

                const vendorSubTotals = resolveGeneralVendorSubTotals(
                  siteDetail,
                  apiData,
                );

                if (vendorSubTotals.length !== 0) {
                  const entries = [
                    {
                      srNo: "A1",
                      keySuffix: "basic_price",
                      city: "Basic Price",
                    },
                    {
                      srNo: "B1",
                      keySuffix: "commission_charges",
                      city: "Commission Charges",
                    },
                    {
                      srNo: "C1",
                      keySuffix: "taxes",
                      city: "Taxes",
                    },
                    {
                      srNo: "D1",
                      keySuffix: "total_price",
                      city: "Total Price",
                    },
                    {
                      srNo: "E1",
                      keySuffix: "contingency",
                      city: "Contingency",
                    },
                    {
                      srNo: "F1",
                      keySuffix: "total_with_tax",
                      city: "Total With Tax",
                    },
                    {
                      srNo: "H1",
                      keySuffix: "payment_terms",
                      city: "Payment Terms",
                    },
                    {
                      srNo: "I1",
                      keySuffix: "payment_security_details",
                      city: "Payment Security Details",
                    },
                    {
                      srNo: "K1",
                      keySuffix: "commercial_ranking",
                      city: "Commercial Ranking on Total Price",
                    },
                  ];

                  entries.forEach((entry) => {
                    const rowData = {
                      key: `${entry.keySuffix}-${uniqueKeyCounter++}`,
                      city: entry.city,
                      srNo: entry.srNo,
                    };

                    let lowest = null;
                    let secondLowest = null;
                    let thirdLowest = null;

                    if (entry.keySuffix === "commercial_ranking") {
                      const latestRows =
                        getLatestVendorSubTotalRows(vendorSubTotals);
                      const commercialRankingValues = latestRows
                        .map((vendor) => vendor.commercial_ranking)
                        .filter((value) => typeof value === "number");

                      const sortedRankingValues = [
                        ...new Set(commercialRankingValues),
                      ].sort((a, b) => a - b);

                      lowest = sortedRankingValues[0] ?? null;
                      secondLowest = sortedRankingValues[1] ?? null;
                      thirdLowest = sortedRankingValues[2] ?? null;
                    }

                    const latestRoundByVendorId = new Map(
                      getLatestVendorSubTotalRows(vendorSubTotals).map((row) => [
                        row.vendor_id,
                        row.round,
                      ]),
                    );

                    const isVendorLatestRankingRound = (vendorDetail) =>
                      latestRoundByVendorId.get(vendorDetail.vendor_id) ===
                      vendorDetail.round;

                    const renderCommercialRankingBadge = (vendorDetail) => {
                      if (!isVendorLatestRankingRound(vendorDetail)) {
                        return "";
                      }

                      if (vendorDetail.commercial_ranking === lowest) {
                        return (
                          <Tooltip
                            color={"#094E8F"}
                            placement="top"
                            title={
                              <span style={{ color: "#fff" }}>Lowest Price</span>
                            }
                          >
                            <span className="lowest">L1</span>
                          </Tooltip>
                        );
                      }

                      if (vendorDetail.commercial_ranking === secondLowest) {
                        return (
                          <Tooltip
                            color={"#094E8F"}
                            placement="top"
                            title={
                              <span style={{ color: "#fff" }}>
                                Second Lowest Price
                              </span>
                            }
                          >
                            <span className="secondLowest">L2</span>
                          </Tooltip>
                        );
                      }

                      if (vendorDetail.commercial_ranking === thirdLowest) {
                        return (
                          <Tooltip
                            color={"#094E8F"}
                            placement="top"
                            title={
                              <span style={{ color: "#fff" }}>
                                Third Lowest Price
                              </span>
                            }
                          >
                            <span className="thirdLowest">L3</span>
                          </Tooltip>
                        );
                      }

                      return vendorDetail.commercial_ranking ?? "";
                    };

                    const roundPriority = ["r1", "r", "r0"];
                    const latestRoundMap = {};

                    vendorSubTotals.forEach((detail) => {
                      const { vendor_id, round, basic_price } = detail;
                      if (basic_price === 0) return;
                      if (
                        !latestRoundMap[vendor_id] ||
                        roundPriority.indexOf(round) <
                          roundPriority.indexOf(latestRoundMap[vendor_id].round)
                      ) {
                        latestRoundMap[vendor_id] = detail;
                      }
                    });

                    ["r0", "r1", "r"].forEach((round) => {
                      const roundDetails = vendorSubTotals.filter(
                        (vendorDetail) => vendorDetail.round === round,
                      );

                      roundDetails.forEach((vendorDetail) => {
                        const vendorKey = vendorDetail.vendor_name
                          .toLowerCase()
                          .replace(/\s+/g, "");

                        rowData[`${vendorKey}Amount${round.toUpperCase()}`] =
                          typeof vendorDetail[entry.keySuffix] !== "string"
                            ? entry.keySuffix === "contingency" ||
                              entry.keySuffix === "commission_charges"
                              ? `${vendorDetail[entry.keySuffix]?.toLocaleString(
                                  "en-IN",
                                  { maximumFractionDigits: 0 },
                                )}`
                              : entry.keySuffix === "commercial_ranking"
                                ? renderCommercialRankingBadge(vendorDetail)
                                : entry.keySuffix !== "commercial_ranking"
                                  ? `${vendorDetail[
                                      entry.keySuffix
                                    ]?.toLocaleString("en-IN", {
                                      maximumFractionDigits: 0,
                                    })}`
                                  : vendorDetail[entry.keySuffix]
                            : entry.keySuffix !== "commercial_ranking" &&
                              vendorDetail[entry.keySuffix];

                        rowData[`${vendorKey}Amount`] =
                          entry.keySuffix === "commercial_ranking"
                            ? renderCommercialRankingBadge(vendorDetail)
                            : entry.keySuffix !== "commercial_ranking"
                              ? vendorDetail[
                                  entry.keySuffix
                                ]?.toLocaleString("en-IN", {
                                  maximumFractionDigits: 0,
                                }) || 0
                              : vendorDetail[entry.keySuffix];
                      });
                    });

                    dataSource.push(rowData);
                  });
                }

                return siteDetail.rfq_material_wise_details;
              }
            );
          }
        );

      const vendorSummaryTotals = collectVendorSummary(apiData);
      const summaryRows = buildVendorSummaryTableRows(vendorSummaryTotals);

      setDataSource(dataSource);
      setData(dataSource);
      setSummaryDataSource(summaryRows);
      setVendors(vendors);

      if (state.material_type_names.length === 0) {
        const materialTypes =
          apiData.comparative_details_by_rfq_site_wise.flatMap(
            (cityItem) =>
              cityItem.rfq_site_wise_details.flatMap((siteItem) =>
                siteItem.rfq_material_wise_details.map((materialItem) => ({
                  id: materialItem.rfq_material_type_id,
                  name: materialItem.rfq_material_type_name,
                }))
              )
          );

        const uniqueMaterialTypes = materialTypes.filter(
          (material, index, self) =>
            index ===
            self.findIndex(
              (m) => m.id === material.id && m.name === material.name
            )
        );
        setMaterialName(uniqueMaterialTypes);
      }
    }

    setapiLoading(false);
  }, [response1]);

  const uniqueSummaryVendors = new Set();
  const summaryColumn = Array.isArray(vendors)
    ? vendors.flat(2).flatMap((material) => {
        const materialVendors = material.vendors || [];
        return materialVendors
          .filter((vendor) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");

            if (uniqueSummaryVendors.has(vendorKey)) {
              return false;
            } else {
              uniqueSummaryVendors.add(vendorKey);
              return true;
            }
          })
          .map((vendor) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");

            const bidDates = vendorBidDates[vendorKey] || {};

            const hasFinalBid =
              bidDates.finalBidDate && bidDates.finalBidDate !== "N/A";
            const hasRevisedBid =
              bidDates.revisedBidDate && bidDates.revisedBidDate !== "N/A";
            const hasInitialBid =
              bidDates.initialBidDate && bidDates.initialBidDate !== "N/A";

            let round = "";

            if (hasFinalBid) {
              round = "R - Final";
            } else if (!hasFinalBid && hasRevisedBid) {
              round = "R1 - Revised";
            } else {
              round = "R0 - Initial";
            }
            const color = " #094E8F";
            return {
              title: <span style={{ color }}>{vendor.vendor_name}</span>,
              children: [
                {
                  title: round,
                  align: "center",
                  children: [
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}Amount`,
                      key: `${vendorKey}Amount`,
                      align: "right",
                      render: (text, record, index) => {
                        return text;
                      },
                    },
                  ],
                },
              ],
            };
          });
      })
    : [];

  const uniqueVendors = new Set();
  const uniqueVendors2 = new Set();

  const venColumn = Array.isArray(vendors)
    ? vendors?.flat(2)?.flatMap((material) => {
        const materialVendors = material.vendors || [];

        return materialVendors
          .filter((vendor) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");
            if (uniqueVendors.has(vendorKey)) {
              return false;
            } else {
              uniqueVendors.add(vendorKey);
              return true;
            }
          })
          .map((vendor, venIndex) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");

            const { shouldShowFinal, shouldShowR1, shouldShowR0 } =
              getVendorBidRoundVisibility(vendor, { showR0, showR1, showR });

            const bidDetail = vendor.r_price[0] || {};
            const unitPrice = `₹ ${bidDetail.r_unit_price || 0}`;
            const amount = `₹ ${bidDetail.r_amount_with_gst || 0}`;

            const bidDetailR0 = vendor.r_price[0] || {};
            const unitPriceR0 = `₹ ${bidDetail.r_unit_price || 0}`;
            const amountR0 = `₹ ${bidDetail.r_amount_with_gst || 0}`;
            const color = " #094E8F";
            return {
              title: <span style={{ color }}>{vendor.vendor_name}</span>,
              className: "vencolumn",
              children: [
                shouldShowFinal && {
                  title: (
                    <>
                      <p>R - Final</p>
                      <p>{vendorBidDates[vendorKey]?.finalBidDate || "N/A"}</p>
                    </>
                  ),
                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPrice`,
                      key: `${vendorKey}UnitPrice`,
                      className: "vencolumn",
                      width: 80,
                      align: "right",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}Amount`,
                      key: `${vendorKey}Amount`,
                      width: 80,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                  ].filter(Boolean),
                },
                // showR1 &&
                shouldShowR1 && {
                  title: (
                    <>
                      <p>R1 - Revised</p>
                      <p>
                        {vendorBidDates[vendorKey]?.revisedBidDate || "N/A"}
                      </p>
                    </>
                  ),
                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPriceR1`,
                      key: `${vendorKey}UnitPriceR1`,
                      className: "vencolumn",
                      align: "right",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}AmountR1`,
                      key: `${vendorKey}AmountR1`,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                  ].filter(Boolean),
                },
                // showR0 &&
                shouldShowR0 && {
                  title: (
                    <>
                      <p>R0 - Initial</p>
                      <p>
                        {vendorBidDates[vendorKey]?.initialBidDate || "N/A"}
                      </p>
                    </>
                  ),

                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPriceR0`,
                      key: `${vendorKey}UnitPriceR0`,
                      className: "vencolumn",
                      align: "right",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}AmountR0`,
                      key: `${vendorKey}AmountR0`,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                  ].filter(Boolean),
                },
              ].filter(Boolean),
            };
          });
      })
    : [];

  const hiddenvenColumn = Array.isArray(vendors)
    ? vendors?.flat(2)?.flatMap((material) => {
        const materialVendors = material.vendors || [];

        return materialVendors
          .filter((vendor) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");
            if (uniqueVendors2.has(vendorKey)) {
              return false;
            } else {
              uniqueVendors2.add(vendorKey);
              return true;
            }
          })
          .map((vendor, venIndex) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");

            const { shouldShowFinal, shouldShowR1, shouldShowR0 } =
              getVendorBidRoundVisibility(vendor, { showR0, showR1, showR });

            const bidDetail = vendor.r_price[0] || {};
            const unitPrice = `₹ ${bidDetail.r_unit_price || 0}`;
            const amount = `₹ ${bidDetail.r_amount_with_gst || 0}`;

            const bidDetailR0 = vendor.r_price[0] || {};
            const unitPriceR0 = `₹ ${bidDetail.r_unit_price || 0}`;
            const amountR0 = `₹ ${bidDetail.r_amount_with_gst || 0}`;
            const color = " #094E8F";
            return {
              title: <span style={{ color }}>{vendor.vendor_name}</span>,
              className: "vencolumn",
              children: [
                {
                  title: (
                    <>
                      <p>R - Final</p>
                      <p>{vendorBidDates[vendorKey]?.finalBidDate || "N/A"}</p>
                    </>
                  ),
                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPrice`,
                      key: `${vendorKey}UnitPrice`,
                      className: "vencolumn",
                      width: 80,
                      align: "right",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}Amount`,
                      key: `${vendorKey}Amount`,
                      width: 80,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                  ],
                },
                {
                  title: (
                    <>
                      <p>R1 - Revised</p>
                      <p>
                        {vendorBidDates[vendorKey]?.revisedBidDate || "N/A"}
                      </p>
                    </>
                  ),
                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPriceR1`,
                      key: `${vendorKey}UnitPriceR1`,
                      className: "vencolumn",
                      align: "right",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}AmountR1`,
                      key: `${vendorKey}AmountR1`,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                  ],
                },
                {
                  title: (
                    <>
                      <p>R0 - Initial</p>
                      <p>
                        {vendorBidDates[vendorKey]?.initialBidDate || "N/A"}
                      </p>
                    </>
                  ),

                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPriceR0`,
                      key: `${vendorKey}UnitPriceR0`,
                      className: "vencolumn",
                      align: "right",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}AmountR0`,
                      key: `${vendorKey}AmountR0`,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row) => renderVendorPriceCell(value, row),
                    },
                  ],
                },
              ].filter(Boolean),
            };
          });
      })
    : [];

  const getCustomText = (text, record) => {
    if (record.city === "Basic Price") {
      return `${text}`;
    } else if (record.city === "Taxes") {
      return (
        <Tooltip
          color={"#094E8F"}
          placement="top"
          title={
            <span style={{ color: "#fff" }}>Sum of basic amount * GST</span>
          }
        >
          {`${text}`}
        </Tooltip>
      );
    } else if (record.city === "Commission Charges") {
      return (
        <Tooltip
          color={"#094E8F"}
          placement="top"
          title={
            <span style={{ color: "#fff" }}>
              Sum of basic amount * commission charge from business setting
            </span>
          }
        >
          {`${text}`}
        </Tooltip>
      );
    } else if (record.city === "Contingency") {
      return (
        <Tooltip
          color={"#094E8F"}
          placement="top"
          title={
            <span style={{ color: "#fff" }}>
              Sum of basic amount * Contingency charge from business setting
            </span>
          }
        >
          {text} @ {contengency}% on [
          <Tooltip
            color={"#094E8F"}
            placement="right"
            title={<span style={{ color: "#fff" }}>Total Evaluated Price</span>}
          >
            <span style={{ backgroundColor: "#FFD3B3", padding: "5px" }}>
              D1
            </span>
          </Tooltip>
          ]
        </Tooltip>
      );
    } else if (record.city === "Total Without Tax") {
      return (
        <>
          {text} [
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Basic Price</span>}
          >
            <span style={{ backgroundColor: "#A3F2EB", padding: "5px" }}>
              A1
            </span>
          </Tooltip>
          ]
        </>
      );
    } else if (record.city === "Commercial Ranking on Total Price") {
      return (
        <>
          {text} [
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Total Evaluated Price</span>}
          >
            <span style={{ backgroundColor: "#FFD3B3", padding: "5px" }}>
              D1
            </span>
          </Tooltip>
          ]
        </>
      );
    } else if (record.city === "Total Price") {
      return (
        <>
          {"Total Evaluated Price"} [
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Basic Price</span>}
          >
            <span style={{ backgroundColor: "#A3F2EB", padding: "5px" }}>
              A1
            </span>
          </Tooltip>{" "}
          +{" "}
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Commission Charges</span>}
          >
            <span style={{ backgroundColor: "#CCD5FF", padding: "5px" }}>
              B1
            </span>
          </Tooltip>{" "}
          +{" "}
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Taxes</span>}
          >
            <span style={{ backgroundColor: "#FFEFA8", padding: "5px" }}>
              C1
            </span>
          </Tooltip>
          ]
        </>
      );
    } else if (record.city === "Total With Tax") {
      return (
        <>
          {"Total Proposed Cost"} [
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Total Evaluated Price</span>}
          >
            <span style={{ backgroundColor: "#FFD3B3", padding: "5px" }}>
              D1
            </span>
          </Tooltip>{" "}
          +{" "}
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Contingency</span>}
          >
            <span style={{ backgroundColor: "#FF9E96", padding: "5px" }}>
              E1
            </span>
          </Tooltip>
          ]
        </>
      );
    }
    return text;
  };

  const predefinedColors = {
    A1: "#A3F2EB",
    B1: "#CCD5FF",
    C1: "#FFEFA8",
    D1: "#FFD3B3",
    E1: "#FF9E96",
    F1: "#F4B9F4",
    G1: "#CCE5E5",
    H1: "#E1FF97",
    I1: "#ADFFC7",
    K1: "rgb(255 231 231)",
    L: "rgb(255 157 43)",
    L1: "rgb(225 127 43)",
    M: "rgb(155 157 3)",
    M1: "rgb(255 245 84)",
  };

  const getColor = (text) => {
    if (/^\d/.test(text)) {
      return null;
    }

    if (predefinedColors[text]) {
      return predefinedColors[text];
    }

    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };

  const parseEarthingStrip = (name) => {
    const match = name.match(/(\d+)\s*x\s*(\d+)\s*[-–]\s*(\w+)/i);
    if (!match) return null;
    return {
      width: parseFloat(match[1]),
      thickness: parseFloat(match[2]),
      material: match[3].toUpperCase(), // CU or GI
    };
  };

  const columns = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      key: "srNo",
      width: 40,
      align: "center",
      render: (text) => {
        const bgColor = getColor(text);
        return (
          <span
            style={{
              backgroundColor: bgColor || "transparent",
              height: "25px",
              width: "25px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Material",
      dataIndex: "items",
      key: "items",
      width: 160,
      render: (text, record, index) => {
        const a1Index = dataSource1.findIndex((row) => row.srNo === "A1");

        if (index >= a1Index && a1Index !== -1) {
          return {
            children: getCustomText(record.city || text, record),
            props: {
              colSpan: 1,
            },
          };
        }
        return text;
      },
    },
    showGST && {
      title: (
        <>
          <p>GST</p>
          <p>(%)</p>
        </>
      ),
      dataIndex: "gst_rate",
      key: "gst_rate",
      width: 40,
      align: "center",
    },
    {
      title: "Qty.",
      dataIndex: "qty",
      key: "qty",
      align: "center",
      // width: 40,
      width: "auto",
      render: (text, record, index) => {
        return text?.toLocaleString("en-IN", { maximumFractionDigits: 0 });
      },
    },
    {
      title: "UoM.",
      dataIndex: "uom",
      key: "uom",
      align: "center",
      // width: 40,
      width: "auto",
      render: (text, record) => {
        if (text === "Cubic Meter") {
          return "Cu. Mtr.";
        } else {
          return text;
        }
      },
    },
    showLPP && {
      title: "LPP (Last Purchase Price)",
      children: [
        {
          title: "Last order detail",
          children: [
            {
              title:
                // <>
                // <p>PO & </p>
                // <p>Date</p>

                // </>

                "PO & Date",
              dataIndex: "poDate",
              key: "poDate",
              width: 90,
              render: (text, record) => {
                if (!text) return "";
                const [poNumber, date] = text.split(" - ");

                return (
                  <>
                    <p style={{ fontSize: "12px" }}>{poNumber}</p>
                    <p style={{ fontSize: "12px" }}>{date}</p>
                  </>
                );
              },
            },
            {
              title: "Vendor Name",
              dataIndex: "vendorName",
              key: "vendorName",
              width: 90,
            },
            {
              title: "Unit Price",
              dataIndex: "unitPrice",
              key: "unitPrice",
              width: 80,
              align: "right",
            },
          ],
        },
        {
          title: (
            <Tooltip
              color="#094E8F"
              placement="top"
              title="Last 30 Days Avg. Price"
            >
              30DAP{" "}
              <InfoCircleOutlined
                style={{ color: "#094E8F", marginLeft: "3px" }}
              />
            </Tooltip>
          ),

          width: 100,
          align: "right",
          children: [
            {
              title: "",
              dataIndex: "avgPrice",
              key: "avgPrice",
              width: 100,
              align: "right",
            },
          ],
        },
      ],
    },
    {
      title: (
        <Tooltip color="#094E8F" placement="top" title="Item wise L1">
          IWL1{" "}
          <InfoCircleOutlined style={{ color: "#094E8F", marginLeft: "3px" }} />
        </Tooltip>
      ),
      children: [
        {
          title: "",
          dataIndex: "itemWiseL1",
          key: "itemWiseL1",
          width: 100,
          align: "right",
          render: (text, record) => {
            return <strong>{text}</strong>;
          },
        },
      ],
    },
    categoryTree !== "Earthing" && {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title="Last 30 Days CityWise Avg. Price on Smart Buyer"
        >
          30DAP {""}{" "}
          <InfoCircleOutlined style={{ color: "#094E8F", marginLeft: "3px" }} />
        </Tooltip>
      ),
      width: 80,
      children: [
        {
          title: "",
          dataIndex: "cityWise",
          width: 120,
          key: "cityWise",
          align: "right",
        },
      ],
    },
    ...venColumn,
  ].filter(Boolean);

  const hiddencolumns = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      key: "srNo",
      width: 40,
      align: "center",
      render: (text) => {
        const bgColor = getColor(text);
        return (
          <span
            style={{
              backgroundColor: bgColor || "transparent",
              height: "25px",
              width: "25px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Material",
      dataIndex: "items",
      key: "items",
      width: 160,
      render: (text, record, index) => {
        const a1Index = dataSource1.findIndex((row) => row.srNo === "A1");

        if (index >= a1Index && a1Index !== -1) {
          return {
            children: getCustomText(record.city || text, record),
            props: { colSpan: 1 },
          };
        }
        return text;
      },
    },
    {
      title: "Qty.",
      dataIndex: "qty",
      key: "qty",
      align: "center",
      // width: 40,
      width: "auto",
      render: (text, record, index) => {
        return text?.toLocaleString("en-IN", { maximumFractionDigits: 0 });
      },
    },
    {
      title: "UoM.",
      dataIndex: "uom",
      key: "uom",
      align: "center",
      // width: 40,
      width: "auto",
      render: (text, record) => {
        if (text === "Cubic Meter") {
          return "Cu. Mtr.";
        } else {
          return text;
        }
      },
    },
    {
      title: (
        <Tooltip color="#094E8F" placement="top" title="Item wise L1">
          IWL1{" "}
          <InfoCircleOutlined style={{ color: "#094E8F", marginLeft: "3px" }} />
        </Tooltip>
      ),
      children: [
        {
          title: "",
          dataIndex: "itemWiseL1",
          key: "itemWiseL1",
          width: 100,
          align: "right",
        },
      ],
    },
    {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title="Last 30 Days CityWise Avg. Price on Smart Buyer"
        >
          30DAP {""}{" "}
          <InfoCircleOutlined style={{ color: "#094E8F", marginLeft: "3px" }} />
        </Tooltip>
      ),
      width: 80,
      children: [
        {
          title: "",
          dataIndex: "cityWise",
          width: 120,
          key: "cityWise",
          align: "right",
        },
      ],
    },
    ...hiddenvenColumn,
  ].filter(Boolean);

  const handleCheckboxhideR1 = () => {
    setShowR0(!showR0);
  };

  const handleCheckboxhideR = () => {
    setShowR1(!showR1);
  };

  const handleCheckboxhideLPP = () => {
    setShowLPP(!showLPP);
  };

  const handleCheckboxhideGST = () => {
    setShowGST(!showGST);
  };

  const handleCheckboxhideRR = () => {
    setHideRBidPrice(!hideRBidPrice);
  };

  const updateState = async () => {
    if (!id) return;
    try {
      setapiLoading(true);
      const payload = buildGeneralComparativeFilterPayload(state);
      const response1 = await patchRequest(
        `general-comparative-rfq/${id}/get-comparative-details-by-city-wise/`,
        payload,
      );
      setResponse1(response1);
    } catch (error) {
      console.error("Failed to apply filters:", error);
      ErrorNotificationMsg(t("Error in Comparatives"));
    } finally {
      setapiLoading(false);
    }
  };

  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
    } else {
      updateState();
    }
  }, [state]);

  const handleMaterialSelectChange = async (value) => {
    const updatedState2 = { ...state, material_type_names: value };
    setState(updatedState2);
  };

  const columns3 = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      key: "srNo",
    },
    {
      title: "",
      dataIndex: "title",
      key: "title",
      render: (text, record, index) => {
        return text?.toLocaleString("en-IN", { maximumFractionDigits: 0 });
      },
    },
    ...summaryColumn,
  ];

  const downloadExcel = async () => {
    const worksheet = XLSX.utils.json_to_sheet(dataSource1);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Table Data");
    XLSX.writeFile(workbook, `comparatives-rfq_city_wise${+new Date()}.xlsx`);
  };

  const exportToExcel = async () => {
    const canvas = await html2canvas(tableRef.current, { scale: 2 });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const headline = "Comparatives - City Wise";
    pdf.setFontSize(18);
    const textWidth = pdf.getTextWidth(headline);
    const centerX = (pageWidth - textWidth) / 2;
    const centerY = pageHeight / 2 - 20;

    pdf.text(headline, centerX, 20);
    const imgWidth = 190;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 10, 30, imgWidth, imgHeight);
    pdf.save(`Comparatives-${rfqNumber}`);
  };

  const exportToPdf = () => {
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a3",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();

    const headline = "Comparatives - City Wise";
    pdf.setFontSize(12);
    const textWidth = pdf.getTextWidth(headline);
    pdf.text(headline, (pageWidth - textWidth) / 2, 15);

    if (!tableRef.current) return;

    let headers = [];
    const headerCells = tableRef.current.querySelectorAll("thead tr th");

    let staticHeaders = ["Sr. No.", "City", "Site", "Item", "Quantity", "UoM"];
    headers.push(...staticHeaders);

    let vendorHeaders = [];
    headerCells.forEach((cell, index) => {
      let text = cell.innerText.trim();
      if (index >= staticHeaders.length) {
        text = text.replace(/\d{2}-\d{2}-\d{2}/g, "").trim(); // Remove date
        if (!text.includes("Unit Rate") && !text.includes("Basic Amount")) {
          vendorHeaders.push(`${text} - Unit Rate`);
          vendorHeaders.push(`${text} - Basic Amount`);
        }
      }
    });

    headers.push(...vendorHeaders);
    const data = [];
    const rows = tableRef.current.querySelectorAll("tbody tr");

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const rowData = [];
      const cells = row.querySelectorAll("td");

      cells.forEach((cell, index) => {
        if (index < staticHeaders.length) {
          rowData.push(cell.innerText.trim());
        }
      });

      let vendorData = [];
      for (let i = staticHeaders.length; i < cells.length; i += 2) {
        const unitRate = cells[i]?.innerText.trim() || "-";
        const basicAmount = cells[i + 1]?.innerText.trim() || "-";
        vendorData.push(unitRate, basicAmount);
      }

      rowData.push(...vendorData);
      data.push(rowData);
    }

    pdf.autoTable({
      head: [headers],
      body: data,
      startY: 20,
      styles: { fontSize: 8, cellPadding: 3, overflow: "linebreak" },
      theme: "grid",
      precision: 100,
      headStyles: {
        fillColor: [0, 122, 255],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 30, left: 10, right: 10 },
      columnStyles: {
        0: { cellWidth: 15 }, // Serial No.
        1: { cellWidth: 40 }, // City
        2: { cellWidth: 40 }, // Site
        3: { cellWidth: 30 }, // Item
        4: { cellWidth: 25 }, // Quantity
        5: { cellWidth: 25 }, // UoM
        ...vendorHeaders.reduce((cols, header, index) => {
          cols[staticHeaders.length + index] = { cellWidth: 25 };
          return cols;
        }, {}),
      },
      didDrawPage: (data) => {
        pdf.setFontSize(10);
        pdf.setTextColor(0);
      },
    });

    pdf.save(`comparatives_city_wise_${Date.now()}.pdf`);
  };

  const menu = (
    <Menu className="downloadmenu">
      <Menu.ItemGroup
        key="pdf"
        title={
          <span style={{ fontWeight: "bold" }}>
            <img src={PDF} alt="PDF Icon" /> Download PDF
          </span>
        }
      >
        <Menu.Item key="pdf1" onClick={exportToExcel}>
          1.1 Download as per current view
        </Menu.Item>
      </Menu.ItemGroup>

      <Menu.ItemGroup
        key="excel"
        title={
          <span style={{ fontWeight: "bold" }}>
            <img src={Excel} alt="PDF Icon" />
            Download Excel
          </span>
        }
      >
        <DownloadTableExcel
          filename={`Comparative City Wise Statement-${rfqNumber}`}
          sheet="users"
          currentTableRef={tableRef.current}
        >
          <Menu.Item key="excel2">2.1. Download as per current view</Menu.Item>
        </DownloadTableExcel>

        <Menu.Item key="excel2">
          <DownloadTableExcel
            filename={`Comparative City Wise Statement-${rfqNumber}`}
            sheet="users"
            currentTableRef={hiddenTableRef.current}
          >
            2.2. Download all offers (BIDs)
          </DownloadTableExcel>
        </Menu.Item>
      </Menu.ItemGroup>
    </Menu>
  );

  const handleExcelDownload = async () => {
    const endpoint = `comparative-rfq/${id}/comparative-excel-material-download/`;
    const fileType = "csv";
    try {
      await getRequest(endpoint)
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "text/csv",
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `comparatives-rfq_${+new Date()}.${fileType}`;
          link.click();
        })
        .catch((error) => {
          //ErrorNotificationMsg(t(`err_${fileType}_download`));
        });
    } catch (error) {
      //setBtnloading(false);
      //ErrorNotificationMsg(error);
    }
  };

  function parseEarthingMaterial(name = "") {
    const sizeMatch = name.match(/(\d+)\s*x\s*(\d+)/i);
    const material = name.toUpperCase().includes("CU")
      ? "CU"
      : name.toUpperCase().includes("GI")
      ? "GI"
      : "";

    return {
      width: sizeMatch ? Number(sizeMatch[1]) : 0,
      thickness: sizeMatch ? Number(sizeMatch[2]) : 0,
      material,
    };
  }

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Comparatives - Material Wise</h3>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/general-procurement-list">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/general-procurement-list">General Procurement</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Comparatives - Material Wise</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <h5 className="rfq-number new">
            RFQ Number :{" "}
            <span style={{ marginRight: "20px" }}>{rfqNumber}</span>
            Buyer : <span>{buyerName}</span>
          </h5>
          
          <div className="btnwrp2" style={{ alignItems: "center" }}>
            <h5 className="rfq-number new">Amount in INR</h5>
            <Dropdown overlay={menu} trigger={["hover"]} placement="bottom">
              <Button
                className="SaveGradiantBtn"
                type="primary"
                htmlType="PDF"
                label="PDF"
                icon={<img src={Downloadpd} alt="PDF Icon" />}
              >
                Download
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>
      <div className="stepscontentwrap">
        <div className="stepsContent">
          <section className="grid-sec mt-5 ">
            <div className="PR-body" style={{ padding: "0" }}>
              <div
                className="rgq-num-wrap"
                style={{ flexWrap: "wrap", alignItems: "center" }}
              >
                <div className="bidleftfilters">
                  <Checkbox checked={showR0} onChange={handleCheckboxhideR1}>
                    Show Initial BID Price - R0
                  </Checkbox>
                  <Checkbox checked={showR1} onChange={handleCheckboxhideR}>
                    Show Revised BID Price - R1
                  </Checkbox>
                  <Checkbox checked={showLPP} onChange={handleCheckboxhideLPP}>
                    Show Last Purchase Price
                  </Checkbox>
                  <Checkbox checked={showGST} onChange={handleCheckboxhideGST}>
                    Show GST %
                  </Checkbox>
                </div>
                <div
                  className="bidlRightfilters"
                  style={{ flexWrap: "nowrap" }}
                >
                  <label>Material Filter</label>
                  <Form.Item name="material_type" label="">
                    <Select
                      showArrow
                      mode="multiple"
                      showSearch
                      allowClear
                      placeholder="Select Material"
                      optionFilterProp="children"
                      onChange={handleMaterialSelectChange}
                      value={state.material_type_names}
                      style={{ width: "200px" }}
                    >
                      {materialName.map((material) => (
                        <Option key={material.id} value={material.name}>
                          {material.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  
                </div>
              </div>

              <Spin spinning={apiLoading}>
                <div ref={tableRef}>
                  <Table
                    ref={tableRef}
                    dataSource={dataSource1}
                    bordered
                    scroll={{ x: "auto" }}
                    columns={columns}
                    className="comparativecustomtbl"
                    pagination={false}
                  />
                </div>
              </Spin>
              <div className="footerTable">
                <Table
                  columns={columns3}
                  dataSource={summaryDataSource}
                  rowKey="key"
                  pagination={false}
                  bordered
                />
              </div>
            </div>
            <div ref={hiddenTableRef} style={{ display: "none" }}>
              <Table
                ref={hiddenTableRef}
                dataSource={dataSource1}
                bordered
                scroll={{ x: "auto" }}
                columns={hiddencolumns}
                className="comparativecustomtbl"
                pagination={false}
                // sticky
              />
            </div>
          </section>
        </div>
        <div
          className="FormFooter bidftr"
          style={{ padding: "16px  ", margin: "0" }}
        >
          <ButtonField
            className="SaveGradiantBtn"
            type="primary"
            label="Price Comparative Review"
            onClick={() =>
              navigate(`/general-procurement/summarypricecomparatives/${id}`)
            }
          />
        </div>
      </div>
    </>
  );
};

export default GeneralProcurementComparative;
